import { tileFromMjlogId } from "../tiles";
import type { ParseResult } from "../types";

// Converts a Tenhou mjlog (XML) into mjai events. The document is a flat
// sequence of self-closing tags, so a regex tokenizer is sufficient (and works
// in Node tests without a DOM). Reference: specs/MJLOG.md.

interface Tag {
    name: string;
    attrs: Record<string, string>;
}

const TAG_RE = /<([A-Za-z0-9]+)((?:\s+[A-Za-z0-9]+\s*=\s*"[^"]*")*)\s*\/?>/g;
const ATTR_RE = /([A-Za-z0-9]+)\s*=\s*"([^"]*)"/g;

function decodeEntities(s: string): string {
    return s.replace(/&(amp|lt|gt|quot|apos|#x[0-9a-fA-F]+|#\d+);/g, (_, ent: string) => {
        switch (ent) {
            case "amp":
                return "&";
            case "lt":
                return "<";
            case "gt":
                return ">";
            case "quot":
                return '"';
            case "apos":
                return "'";
            default:
                return String.fromCodePoint(
                    ent.startsWith("#x") ? parseInt(ent.slice(2), 16) : parseInt(ent.slice(1), 10),
                );
        }
    });
}

function tokenize(xml: string): Tag[] {
    const tags: Tag[] = [];
    for (const m of xml.matchAll(TAG_RE)) {
        const name = m[1] as string;
        if (name === "mjloggm") continue;
        const attrs: Record<string, string> = {};
        for (const a of (m[2] as string).matchAll(ATTR_RE)) {
            attrs[a[1] as string] = decodeEntities(a[2] as string);
        }
        tags.push({ name, attrs });
    }
    return tags;
}

function decodeName(encoded: string): string {
    try {
        return decodeURIComponent(encoded);
    } catch {
        return encoded;
    }
}

const ints = (s: string): number[] => (s === "" ? [] : s.split(",").map(Number));
const tid = tileFromMjlogId;

/** Decode the packed meld attribute `m` of an <N> tag into a mjai call event. */
export function decodeMeld(m: number, who: number): MjaiEvent {
    const target = (who + (m & 3)) % 4;
    if (m & 0x04) {
        // chi: bits 10-15 = base*3 + calledPos, copy indices at bits 3-4/5-6/7-8
        const t = m >> 10;
        const calledPos = t % 3;
        const base = Math.floor(t / 3);
        const suit = Math.floor(base / 7);
        const low = base % 7;
        const ids = [0, 1, 2].map((i) => (suit * 9 + low + i) * 4 + ((m >> (3 + 2 * i)) & 3));
        return {
            type: "chi",
            actor: who,
            target,
            pai: tid(ids[calledPos] as number),
            consumed: ids.filter((_, i) => i !== calledPos).map(tid),
        };
    }
    if (m & 0x08) {
        // pon: bits 9-15 = kind*3 + calledPos, bits 5-6 = the copy NOT in the meld
        const t = m >> 9;
        const calledPos = t % 3;
        const kind = Math.floor(t / 3);
        const unused = (m >> 5) & 3;
        const ids = [0, 1, 2, 3].filter((c) => c !== unused).map((c) => kind * 4 + c);
        return {
            type: "pon",
            actor: who,
            target,
            pai: tid(ids[calledPos] as number),
            consumed: ids.filter((_, i) => i !== calledPos).map(tid),
        };
    }
    if (m & 0x10) {
        // kakan: same layout as pon; bits 5-6 = the copy being added
        const kind = Math.floor((m >> 9) / 3);
        const added = (m >> 5) & 3;
        return {
            type: "kakan",
            actor: who,
            pai: tid(kind * 4 + added),
            consumed: [0, 1, 2, 3].filter((c) => c !== added).map((c) => tid(kind * 4 + c)),
        };
    }
    if (m & 0x20) {
        return { type: "kita", actor: who, pai: "N" };
    }
    // ankan (from self) or daiminkan: bits 8-15 = a tile id of the kan
    const hai0 = m >> 8;
    const kind = Math.floor(hai0 / 4);
    if ((m & 3) === 0) {
        return {
            type: "ankan",
            actor: who,
            consumed: [0, 1, 2, 3].map((c) => tid(kind * 4 + c)),
        };
    }
    return {
        type: "daiminkan",
        actor: who,
        target,
        pai: tid(hai0),
        consumed: [0, 1, 2, 3].filter((c) => c !== hai0 % 4).map((c) => tid(kind * 4 + c)),
    };
}

const RYUKYOKU_REASONS: Record<string, string> = {
    yao9: "kyushu_kyuhai",
    reach4: "suucha_riichi",
    ron3: "sanchahou",
    kaze4: "suufon_renda",
    kan4: "suukaikan",
    nm: "nagashi_mangan",
};

/** sc="s0,d0,s1,d1,..." (units of 100, sN pre-payment) -> {deltas, scores}. */
function parseSc(sc: string | undefined, np: number): { deltas?: number[]; scores?: number[] } {
    if (!sc) return {};
    const v = ints(sc);
    const deltas: number[] = [];
    const scores: number[] = [];
    // Sanma logs still carry a dummy 4th seat (always 0) — trim to np.
    for (let p = 0; p * 2 + 1 < v.length && p < np; p++) {
        deltas.push((v[p * 2 + 1] as number) * 100);
        scores.push(((v[p * 2] as number) + (v[p * 2 + 1] as number)) * 100);
    }
    return { deltas, scores };
}

export function parseMjlog(xml: string): ParseResult {
    const events: MjaiEvent[] = [];
    const warnings: string[] = [];
    const tags = tokenize(xml);
    if (!tags.some((t) => t.name === "INIT")) {
        throw new Error("mjlog contains no INIT tag");
    }

    let np = 4;
    let sawNames = false;
    let sawOwari = false;
    const lastDraw: (number | null)[] = [null, null, null, null];
    let pendingEndKyoku = false;

    const closeKyoku = () => {
        if (pendingEndKyoku) {
            events.push({ type: "end_kyoku" });
            pendingEndKyoku = false;
        }
    };

    for (const { name, attrs } of tags) {
        const draw = /^([TUVW])(\d+)$/.exec(name);
        const discard = /^([DEFG])(\d+)$/.exec(name);
        if (draw) {
            const actor = "TUVW".indexOf(draw[1] as string);
            const id = Number(draw[2]);
            events.push({ type: "tsumo", actor, pai: tid(id) });
            lastDraw[actor] = id;
            continue;
        }
        if (discard) {
            const actor = "DEFG".indexOf(discard[1] as string);
            const id = Number(discard[2]);
            events.push({ type: "dahai", actor, pai: tid(id), tsumogiri: id === lastDraw[actor] });
            continue;
        }
        switch (name) {
            case "GO":
                np = Number(attrs.type ?? 0) & 0x10 ? 3 : 4;
                break;
            case "UN":
                if (!sawNames && attrs.n0 !== undefined) {
                    sawNames = true;
                    const names = [];
                    for (let p = 0; p < np; p++) names.push(decodeName(attrs[`n${p}`] ?? ""));
                    events.push({ type: "start_game", names });
                }
                break;
            case "INIT": {
                closeKyoku();
                const seed = ints(attrs.seed ?? "");
                const ten = ints(attrs.ten ?? "");
                const roundId = seed[0] as number;
                const tehais = [];
                for (let p = 0; p < np; p++) {
                    tehais.push(ints(attrs[`hai${p}`] ?? "").map(tid));
                }
                events.push({
                    type: "start_kyoku",
                    bakaze: "ESWN"[roundId >> 2],
                    kyoku: (roundId % 4) + 1,
                    honba: seed[1],
                    kyotaku: seed[2],
                    oya: Number(attrs.oya),
                    dora_marker: tid(seed[5] as number),
                    scores: ten.slice(0, np).map((x) => x * 100),
                    tehais,
                });
                lastDraw.fill(null);
                break;
            }
            case "N": {
                const ev = decodeMeld(Number(attrs.m), Number(attrs.who));
                events.push(ev);
                if (ev.type === "chi" || ev.type === "pon") {
                    // The next discard comes without a draw; a stale physical id
                    // must not make it look like a tsumogiri.
                    lastDraw[ev.actor as number] = null;
                }
                break;
            }
            case "REACH": {
                const who = Number(attrs.who);
                if (attrs.step === "1") {
                    events.push({ type: "reach", actor: who });
                } else {
                    const ev: MjaiEvent = { type: "reach_accepted", actor: who };
                    if (attrs.ten !== undefined) {
                        ev.scores = ints(attrs.ten).slice(0, np).map((x) => x * 100);
                    }
                    events.push(ev);
                }
                break;
            }
            case "DORA":
                events.push({ type: "dora", dora_marker: tid(Number(attrs.hai)) });
                break;
            case "AGARI": {
                const ev: MjaiEvent = {
                    type: "hora",
                    actor: Number(attrs.who),
                    target: Number(attrs.fromWho ?? attrs.fromwho),
                    pai: tid(Number(attrs.machi)),
                    ...parseSc(attrs.sc, np),
                };
                if (attrs.doraHaiUra !== undefined) {
                    ev.ura_markers = ints(attrs.doraHaiUra).map(tid);
                }
                events.push(ev);
                pendingEndKyoku = true;
                if (attrs.owari !== undefined) sawOwari = true;
                break;
            }
            case "RYUUKYOKU": {
                events.push({
                    type: "ryukyoku",
                    reason: RYUKYOKU_REASONS[attrs.type ?? ""] ?? "exhaustive_draw",
                    ...parseSc(attrs.sc, np),
                });
                pendingEndKyoku = true;
                if (attrs.owari !== undefined) sawOwari = true;
                break;
            }
            case "SHUFFLE":
            case "TAIKYOKU":
            case "BYE":
                break;
            default:
                warnings.push(`ignored unknown mjlog tag <${name}>`);
        }
    }

    closeKyoku();
    if (!sawOwari) warnings.push("log has no owari (game may be truncated)");
    events.push({ type: "end_game" });
    return { events, warnings };
}
